import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import Representants from "@/dashboard/components/financer/Representants";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
  invalidateDashboardQueries,
} from "@/dashboard/dashboardQueryConfig";
import AddFunderDialog from "@/dashboard/components/fundings/AddFunderDialog";
import GiveAccessDialog from "@/dashboard/components/fundings/GiveAccessDialog";
import EditFunderDialog from "@/dashboard/components/fundings/EditFunderDialog";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { autoScrollJoyrideStep } from "@/libs/joyrideAutoScroll";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, MoreHorizontal } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { Link } from "react-router-dom";

type Funders = {
  id: string | number;
  photo: string;
  financeur: string;
  representant: any;
  montant?: number;
  entite?: string;
  nifstat: string;
  siret: string;
  projetfinance?: string;
  access? : boolean
};

const Funders = () => {
  const queryClient = useQueryClient();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const { t } = useTranslation("dashboard", {
    keyPrefix: "financeur",
  });
  const hidePostActions = useHidePostActions();
  const financeursEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/financers`;
  const financeursQueryKey = [dashboardQueryKeys.funders, financeursEndpoint];
  const cachedFinanceurs =
    queryClient.getQueryData<any[]>(financeursQueryKey);
  const financeurs = useFetchDataV2<any[]>(
    financeursEndpoint,
    {
      browserCache: true,
      browserCacheKey: "funders",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 60,
      cacheId: dashboardQueryKeys.funders,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.funders,
    },
  );
  const financeursData = React.useMemo(
    () =>
      Array.isArray(financeurs.data)
        ? financeurs.data
        : Array.isArray(cachedFinanceurs)
          ? cachedFinanceurs
          : [],
    [cachedFinanceurs, financeurs.data],
  );

  const [data, setData] = useState<Funders[]>([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAccesModal, setOpenAccesModal] = useState(false);
  const [toEdit, setToEdit] = useState<any>(null);
  const [toGiveAccess, setToGiveAccess] = useState<any>(null);

  const { postData } = usePostData();
  const { user } = useAuth();

  const [openRep, setOpenRep] = useState(false);
  const [toView, setToView] = useState<any>(null);

  useEffect(() => {
    setData(
      financeursData.length
        ? financeursData.map((funder: any) => ({
            id: funder.id,
            photo: funder.photo
              ? import.meta.env.VITE_IP_API + funder.photo.uri
              : "/image-en-attente.jpg",
            financeur: funder.structureName,
            access: funder.access, 
            representant: (
              <div className="flex gap-2 items-center">
                {funder.representants.length > 0 && (
                  <div
                    style={{
                      width: "calc(100% - 50px)",
                    }}
                    className="truncate"
                  >
                    {funder.representants[0].firstname +
                      " " +
                      funder.representants[0].lastname}
                    <br />
                    <span className="text-[12px] text-muted-foreground">
                      {funder.representants[0].email}
                    </span>
                  </div>
                )}
                {funder.representants.length - 1 > 0 && (
                  <div
                    onClick={() => {
                      setToView(funder);
                      setOpenRep(true);
                    }}
                    className="cursor-pointer bg-primary rounded-full text-background py-0 px-2"
                  >
                    + {funder.representants.length - 1}
                  </div>
                )}
              </div>
            ),
            siret: funder.siret,
            nifstat: funder.nif,
          }))
        : ([] as Funders[]),
    );
  }, [financeursData]);

  const columns: ColumnDef<Funders>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          id="checkbox_funder"
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "photo",
      header: t("Photo"),
      cell: ({ row }) => {
        const url = row.getValue("photo") as string;
        const id = row.original.id;

        return (
          <Link
            to={`/dashboard/financeur/${id}`}
            className="inline-flex items-center"
            title={t("Voir le détail")}
          >
            <img
              src={url}
              alt="photo financeur"
              className="object-cover rounded-full size-8 flex border"
            />
          </Link>
        );
      },
    },
    {
      accessorKey: "financeur",
      header: t("Financeur"),
      cell: ({ row }) => {
        const id = row.original.id;
        const label = row.getValue("financeur") as string;

        return (
          <Link
            to={`/dashboard/financeur/${id}`}
            className="inline-flex items-center gap-2 hover:underline text-[#1d5215] max-w-[300px]"
            title={t("Voir le détail")}
          >
            <span className="truncate">{label}</span>
            <ExternalLink className="w-4 h-4 opacity-70" />
          </Link>
        );
      },
    },
    {
      accessorKey: "siret",
      header: t("Siret"),
      cell: ({ row }) => <div>{row.getValue("siret")}</div>,
    },
    {
      accessorKey: "nifstat",
      header: t("NIF/STAT"),
      cell: ({ row }) => <div>{row.getValue("nifstat")}</div>,
    },
    {
      accessorKey: "representant",
      header: t("Représentant"),
      cell: ({ row }) => {
        return row.getValue("representant");
      },
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => {
        const selectedFunder =
          (
            financeursData as Array<{ id: string | number }> | undefined
          )?.find((funder) => funder.id === row.original.id) ?? row.original;

        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[#1d5215] hover:text-[#1d5215]"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Actions financeur</span>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem
                  onClick={() => {
                    setToView(selectedFunder);
                    setOpenRep(true);
                  }}
                >
                  {hidePostActions ? "Voir les représentants" : "Gérer les représentants"}
                </DropdownMenuItem>

                {!hidePostActions && (
                  <DropdownMenuItem
                    onClick={() => {
                      setToEdit(selectedFunder);
                      setOpenEditModal(true);
                    }}
                  >
                    {t("Modifier")}
                  </DropdownMenuItem>
                )}

                {!hidePostActions &&
                  (
                    <DropdownMenuItem
                      onClick={() => {
                        setToGiveAccess(selectedFunder);
                        setOpenAccesModal(true);
                      }}
                    >
                      {row.original.access ? "Modifier l'accès" : "Créer un accès"}
                    </DropdownMenuItem>
                  )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const [openAddModal, setOpenAddModal] = useState(false);

  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  useEffect(() => {
    if (!openRep) {
      setToView(null);
    }
  }, [openRep]);

  const updateFundersCache = (
    updater: (current: any[] | undefined) => any[] | undefined,
  ) => {
    queryClient.setQueriesData(
      {
        exact: false,
        queryKey: [dashboardQueryKeys.funders],
      },
      updater,
    );
  };

  const snapshotFundersQueries = () =>
    queryClient.getQueriesData<any[]>({
      exact: false,
      queryKey: [dashboardQueryKeys.funders],
    });

  const restoreFundersQueries = (snapshots: [unknown, any[] | undefined][]) => {
    snapshots.forEach(([queryKey, previousData]) => {
      queryClient.setQueryData(queryKey as any, previousData);
    });
  };

  const normalizeFunderResponse = (response: any) =>
    response?.data && typeof response.data === "object"
      ? response.data
      : response;

  const upsertFunderInCache = (response: any, optimisticId?: string | number) => {
    const nextFunder = normalizeFunderResponse(response);
    if (!nextFunder?.id) {
      invalidateDashboardQueries(queryClient, "funders");
      return;
    }

    updateFundersCache((current: any[] | undefined) => {
      const list = Array.isArray(current) ? current : [];
      const exists = list.some(
        (funder) => funder.id === nextFunder.id || funder.id === optimisticId,
      );
      return exists
        ? list.map((funder) =>
            funder.id === nextFunder.id || funder.id === optimisticId
              ? { ...funder, ...nextFunder }
              : funder,
          )
        : [nextFunder, ...list];
    });
    queryClient.setQueryData(
      [
        `funder-${nextFunder.id}`,
        `${import.meta.env.VITE_API_ENDPOINT}/user/${nextFunder.id}`,
      ],
      nextFunder,
    );
    invalidateDashboardQueries(queryClient, "funders");
  };

  const applyOptimisticFunderCreate = (draftFunder: any) => {
    const snapshots = snapshotFundersQueries();
    updateFundersCache((current: any[] | undefined) => [
      draftFunder,
      ...(Array.isArray(current) ? current : []),
    ]);
    return { snapshots, optimisticId: draftFunder.id };
  };

  const applyOptimisticFunderUpdate = (draftFunder: any) => {
    const snapshots = snapshotFundersQueries();
    updateFundersCache((current: any[] | undefined) =>
      Array.isArray(current)
        ? current.map((funder) =>
            funder.id === draftFunder.id ? { ...funder, ...draftFunder } : funder,
          )
        : current,
    );
    return { snapshots, optimisticId: draftFunder.id };
  };

  const rollbackOptimisticFunders = (meta: any) => {
    if (Array.isArray(meta?.snapshots)) {
      restoreFundersQueries(meta.snapshots);
    }
  };

  const handleDelete = async () => {
    setLoadingDelete(true);
    const ids = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    const previousFinanceurs = snapshotFundersQueries();

    updateFundersCache((current: any[] | undefined) =>
      Array.isArray(current)
        ? current.filter((funder) => !ids.includes(funder.id))
        : current,
    );
    setRowSelection({});

    const res = await postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/financers/delete",
      data: { ids },
    });

    if (res && res.success !== false) {
      toast({
        title: t("Les financeurs ont bien été supprimé."),
      });
      setLoadingDelete(false);
      setOpenModalDelete(false);
      invalidateDashboardQueries(queryClient, "funders");
      return;
    }

    restoreFundersQueries(previousFinanceurs);
    setLoadingDelete(false);
  };

  const steps: Step[] = [
    {
      target: "body",
      content: (
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">
            {t("Bienvenue sur la page des financeurs!")}
          </h2>
          <p>
            {t(
              "Cette page vous permet de visualiser et d’ajouter les financeurs liés à vos projets.",
            )}
          </p>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#add_funder",
      title: t("Ajouter un financeur"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Cliquez ici pour ajouter un nouveau financeur. Un formulaire s’ouvrira pour saisir ses informations.",
            )}
          </p>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#table_funder",
      title: t("Liste des financeurs"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Retrouvez ici la liste des financeurs enregistrés, avec leurs informations principales.",
            )}
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#checkbox_funder",
      title: t("Sélectionner des financeurs"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Cochez les cases à gauche pour sélectionner un ou plusieurs financeurs que vous souhaitez supprimer.",
            )}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#delete_funder",
      title: t("Financeurs sélectionnés"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Quand vous sélectionnez des financeurs, cette zone indique combien sont sélectionnés.",
            )}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      target: "#delete_funder",
      title: t("Supprimer la sélection"),
      content: (
        <div className="text-center">
          <p>
            {t(
              "Un bouton « Supprimer la sélection » s’affichera ici lorsque des financeurs seront sélectionnés.",
            )}
          </p>
        </div>
      ),
      placement: "right",
      disableBeacon: true,
      spotlightClicks: true,
    },
  ];

  const [run, setRun] = useState(false);

  useEffect(() => {
    const hasSeenTutorialFunders = localStorage.getItem(
      "hasSeenTutorialFunders",
    );
    if (!hasSeenTutorialFunders || user?.email === "demo@verdeo.fr") {
      setRun(true);
    }
  }, [user]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    autoScrollJoyrideStep(data);
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "skip") {
      setRun(false);
      localStorage.setItem("hasSeenTutorialFunders", "true");
    }
  };

  if (financeurs?.loading && financeursData.length === 0) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        callback={handleJoyrideCallback}
        continuous={false}
        scrollToFirstStep={false}
        disableScrolling={true}
        showProgress={true}
        showSkipButton={true}
        spotlightClicks={false}
        locale={{
          back: t("Précédent"),
          close: t("Suivant"),
          last: t("Terminer"),
          next: t("Suivant"),
          skip: t("Passer"),
        }}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "  #383937",
            overlayColor: "rgba(255, 255, 255, 0.8)",
            spotlightShadow: "0 0 0 rgba(0, 0, 0, 0)",
            textColor: "#fff",
            zIndex: 1000,
          },
          buttonBack: {
            backgroundColor: " #669523",
            color: "white",
            borderRadius: "20px",
            marginRight: "20px",
            width: "100px",
            fontSize: "15px",
          },

          buttonNext: {
            backgroundColor: "#669523",
            color: "white",
            borderRadius: "20px",
            width: "80px",
            fontSize: "15px",
          },
        }}
      />

      <div>
        <Representants open={openRep} setOpen={setOpenRep} funder={toView} />

        <ReactBreadcrumb
          items={[
            {
              href: ``,
              name: t("Mes financeurs"),
            },
          ]}
          isTruncated={false}
        />

        <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
          <h1
            className="mb-2 mt-4 text-[24px] font-medium inline-block"
            id="program-title"
          >
            {t("Mes financeurs")}
          </h1>

          <AddFunderDialog
            open={openAddModal}
            onHandleOpen={setOpenAddModal}
            onOptimisticSave={applyOptimisticFunderCreate}
            onSaveError={rollbackOptimisticFunders}
            onSaveSuccess={(res, meta) => {
              upsertFunderInCache(res, meta?.optimisticId);
              setOpenAddModal(false);
            }}
          />

          {openEditModal && (
            <EditFunderDialog
              open={openEditModal}
              onHandleOpen={setOpenEditModal}
              onOptimisticSave={applyOptimisticFunderUpdate}
              onSaveError={rollbackOptimisticFunders}
              onSaveSuccess={(res, meta) => {
                upsertFunderInCache(res, meta?.optimisticId);
                setOpenEditModal(false);
              }}
              funder={toEdit}
            />
          )}
          {openAccesModal && (
            <GiveAccessDialog
              open={openAccesModal}
              onHandleOpen={setOpenAccesModal}
              funder={toGiveAccess}
             
            />
          )}
        </div>

        <div className="w-full mt-5">
          <div id="table_funder" className="rounded-md border">
            <Table>
              <TableHeader >
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      {t("Pas de résultats.")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              <p id="delete_funder" className="w-fit">
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
                {table.getFilteredRowModel().rows.length}{" "}
                {t("ligne(s) sélectionnée(s).")}
              </p>
              {table.getFilteredSelectedRowModel().rows.length > 0 &&
                !hidePostActions && (
                  <button
                    className="ml-2 text-primary underline"
                    onClick={() => {
                      setOpenModalDelete(true);
                    }}
                  >
                    {t("Supprimer la sélection")}
                  </button>
                )}
            </div>
          </div>
        </div>

        <Dialog open={openModalDelete} onOpenChange={setOpenModalDelete}>
          <DialogContent className="sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto">
            <DialogHeader className="">
              <DialogTitle asChild>
                <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
                  {t("Supprimer les financeurs")}{" "}
                </h2>
              </DialogTitle>
              <DialogDescription asChild className="text-[#595959]">
                <p className="">
                  {t("Voulez-vous vraiment supprimer ces financeurs ?")}
                </p>
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap mt-5 h-full overflow-y-auto">
              <div className="w-full flex justify-end mt-5">
                <DialogFooter className="pb-5">
                  <Button
                    onClick={() => handleDelete()}
                    type="button"
                    disabled={loadingDelete}
                  >
                    {loadingDelete ? "..." : t("Supprimer")}
                  </Button>
                </DialogFooter>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

const LoadingSkeleton = () => (
  <>
    <Skeleton className="h-4 w-48 mb-4" />

    <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
      <Skeleton className="h-8 w-48 mb-2 mt-4" />
      <Skeleton className="h-10 w-40" />
    </div>

    <div className="w-full mt-5">
      <div className="rounded-md border p-4 space-y-4">
        <div className="flex space-x-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-4 w-[25%]" />
          ))}
        </div>

        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex space-x-4 pt-2">
            {[...Array(4)].map((_, j) => (
              <Skeleton key={j} className="h-4 w-[25%]" />
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end space-x-2 py-4">
        <Skeleton className="flex-1 h-4 w-60" />
      </div>
    </div>
  </>
);

export default Funders;
